import * as React from 'react';
import { ArrowDown } from '@sravni/react-icons';
import { useBoolean, useStyles, noop } from '@sravni/react-utils';
import { Icon } from '../../Icon/src/Icon.js';
import { Space } from '../../Space/src/Space.js';
import '../../Transition/src/Fade.js';
import '../../Transition/src/ScaleFade.js';
import '../../Transition/src/SlideFade.js';
import { Collapse } from '../../Transition/src/Collapse.js';
import modules_127cd76a from '../styles/Accordion.module.less.js';
import AccordionContext from './AccordionContext.js';

/**
 * @description Accordion Item component.
 *
 * @component
 * @example
 * ```jsx
 * <Accordion.Item key="credit" title="О кредите">content</Accordion.Item>
 * ```
 */
const Item = React.forwardRef((props, ref) => {
    const { id, title, subtitle, icon, active, tabIndex, unmountOnExit = true, className, children, onChange, ...restProps } = props;
    const activeItemRef = React.useRef(null);
    const { padding, scrollIntoView, onCollapse, size } = React.useContext(AccordionContext);
    const [enableAnimation, setEnableAnimation] = useBoolean(false);
    const cx = useStyles(modules_127cd76a);
    const headerClassNames = cx('header', {
        active,
        [`size-${size}`]: size,
        padding,
    });
    const contentClassNames = cx('content', { [`size-${size}`]: size, padding });
    const collapse = React.useCallback((event) => {
        if (scrollIntoView) {
            activeItemRef.current = event.currentTarget.closest('[data-qa=CollapseItem]');
            setTimeout(() => {
                activeItemRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 300);
        }
        onChange?.(!active);
        onCollapse?.(event);
    }, [scrollIntoView, onChange, active, onCollapse]);
    const handleCollapse = (event) => {
        collapse(event);
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            collapse(event);
        }
    };
    return (React.createElement("div", { ref: ref, "data-qa": "CollapseItem", className: cx(className, 'panel', { active }), ...restProps },
        React.createElement("div", { className: headerClassNames, "data-index": id, tabIndex: tabIndex, onFocus: setEnableAnimation.on, onClick: children ? handleCollapse : noop, onKeyDown: handleKeyDown },
            React.createElement(Space, { size: 12, align: "center" },
                icon && React.createElement("div", { className: cx('icon') }, icon),
                title || subtitle ? (React.createElement(Space, { direction: "vertical", size: 2, className: cx('title-wrapper') },
                    title && React.createElement("div", { className: cx('title') }, title),
                    subtitle && React.createElement("div", { className: cx('subtitle') }, subtitle))) : null),
            children && React.createElement(Icon, { className: cx('collapse-icon'), icon: React.createElement(ArrowDown, null) })),
        React.createElement(Collapse, { visible: active, enableAnimation: enableAnimation, className: contentClassNames, unmountOnExit: unmountOnExit },
            React.createElement("div", { className: cx('content-wrapper') }, children))));
});
Item.defaultProps = {
    tabIndex: 0,
    unmountOnExit: true,
};
Item.displayName = 'Item';

export { Item };
//# sourceMappingURL=AccordionItem.js.map
